"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import type { AssessmentSummary } from "@/types";

type Props = {
  assessments: AssessmentSummary[];
};

/** Fixed locale so SSR + browser hydration always match (undefined locale differs by environment). */
function formatShortDate(iso: string) {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

export function SidebarAssessmentList({ assessments }: Props) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assessments;
    return assessments.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.id.toLowerCase().includes(q) ||
        a.status.toLowerCase().includes(q),
    );
  }, [assessments, query]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <label className="sr-only" htmlFor="assessment-search">
        Search assessments
      </label>
      <div className="relative px-1">
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-(--text-tertiary)"
          aria-hidden
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </span>
        <input
          id="assessment-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search assessments…"
          className="focus-ring w-full rounded-lg border border-(--border-subtle) bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-(--text-tertiary)"
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
          Recent
        </p>
        <ul className="flex flex-col gap-0.5 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <li className="px-2 py-6 text-center text-xs text-(--text-tertiary)">
              No assessments match your search.
            </li>
          ) : (
            filtered.map((a) => (
              <li key={a.id}>
                <Link
                  href={`/assessments/${a.id}`}
                  className="group flex flex-col rounded-lg border border-transparent px-3 py-2.5 transition hover:border-(--border-subtle) hover:bg-(--bg-muted)"
                >
                  <span className="truncate text-sm font-medium text-foreground group-hover:text-(--accent)">
                    {a.title}
                  </span>
                  <span className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-(--text-tertiary)">
                    <span className="capitalize">{a.status}</span>
                    <time dateTime={a.updatedAt}>{formatShortDate(a.updatedAt)}</time>
                  </span>
                </Link>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
